// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';

import { disposeGroup, getViewSizeAtPosition } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';

/**
 * A `THREE.Group` that scales itself so its children maintain a constant fraction
 * of the viewport height, regardless of camera distance or zoom level.
 *
 * Call {@link update} once per frame (after the object's world matrix has been updated)
 * to apply the correct scale. The scale is set to `worldHeight / referenceSize`, where
 * `worldHeight` is the world-space height that corresponds to `screenSizeFraction` of
 * the viewport. Children authored at `referenceSize` will therefore always appear the
 * same size on screen.
 */
export class ViewportScaledGroup extends THREE.Group {
  /**
   * @param screenSizeFraction Fraction of the viewport height the content should occupy.
   *   E.g. `0.035` keeps children at 3.5 % of screen height.
   * @param referenceSize The height at which children are authored (scale = 1).
   *   The group scale is set to `worldHeight / referenceSize`. Defaults to `1`.
   */
  constructor(
    public screenSizeFraction: number,
    public referenceSize: number = 1,
  ) {
    super();
  }

  /**
   * Recomputes and applies a uniform scale so children maintain a constant fraction of
   * the viewport height.
   *
   * - **Perspective**: world height grows linearly with view-space depth.
   * - **Orthographic**: world height scales with the fixed frustum height.
   *
   * Call once per frame after the object's world matrix has been updated.
   */
  update(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera) {
    let worldHeight: number;
    if (camera instanceof THREE.PerspectiveCamera) {
      const worldPos = this.getWorldPosition(new THREE.Vector3());
      worldHeight = this.screenSizeFraction * getViewSizeAtPosition(camera, worldPos).height;
    } else {
      worldHeight = this.screenSizeFraction * (camera.top - camera.bottom);
    }
    this.scale.setScalar(worldHeight / this.referenceSize);
  }

  dispose() {
    disposeGroup(this);
  }
}
